import { addHours, addMinutes } from "date-fns";
import type React from "react";
import { useState } from "react";
import { useController, useFormContext, useFormState, useWatch } from "react-hook-form";

import type {
  CommunityFeedAudienceGroupV2Dto,
  CommunityFeedAudienceProjectConnectionV2Dto,
  CommunityFeedAudienceProjectV2Dto,
  CommunityFeedAudienceV2Dto,
  PostTemplateDto,
} from "@/api/types";
import { AdminCreatePostFormAudienceSelector } from "@/components/AdminCreatePost/AdminCreatePostFormAudienceSelector";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormDatePicker } from "@/components/Form/FormDatePicker";
import { FormErrorWrapper } from "@/components/Form/FormErrorWrapper";
import { FormInput } from "@/components/Form/FormInput";
import { FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import { RadioCard } from "@/components/Radio/RadioCard";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Checkbox } from "@/design-system/Checkbox";
import { FormField } from "@/design-system/FormField";
import { Info } from "@/design-system/Info";
import { Inline } from "@/design-system/Inline";
import { SegmentGroup } from "@/design-system/SegmentGroup";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import { getDefaultEventDates, getEndDateAfterStartChange } from "@/helpers/date-range";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useTranslation } from "@/translations";

import type { AdminCreatePostFormValues } from "../AdminCreatePostPage";
import { AdminCreatePostFormAIHelper } from "./AdminCreatePostFormAIHelper";
import { AdminCreatePostFormEditor } from "./AdminCreatePostFormEditor";
import { AdminCreatePostFormScheduleSelector } from "./AdminCreatePostFormScheduleSelector";
import { AdminCreatePostFormTemplateSelector } from "./AdminCreatePostFormTemplateSelector";
import { InputFrame } from "./InputFrame";

type Section = "date" | "signup" | "audience";

type Props = {
  templates: PostTemplateDto[];
  isErrorTemplates: boolean;
  templateInUse: PostTemplateDto | undefined;
  onApplyTemplate: (input: { template: PostTemplateDto; title: string; content: string }) => void;
  onClearTemplate: () => void;
  isUndoable: boolean;
  isRedoable: boolean;
  isCopilotAvailable: boolean;
  isGenerating: boolean;
  onGenerate: () => void;
  onUndo: () => void;
  onRedo: () => void;
  audience: CommunityFeedAudienceV2Dto;
  selectedGroup: CommunityFeedAudienceGroupV2Dto | undefined;
  selectedProject: CommunityFeedAudienceProjectV2Dto | undefined;
  selectedProjectConnection: CommunityFeedAudienceProjectConnectionV2Dto | undefined;
  onSelectGroup: (group: CommunityFeedAudienceGroupV2Dto) => void;
  onSelectProject: (project: CommunityFeedAudienceProjectV2Dto) => void;
  onSelectProjectConnection: (connection: CommunityFeedAudienceProjectConnectionV2Dto) => void;
  selectedScheduleDate: Date | undefined;
  onSelectScheduleDate: (date: Date | undefined) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  canCreateEvent: boolean;
  canConfigureComments: boolean;
};

/**
 * Renders the redesigned announcement form. Sections are progressively revealed as the author completes
 * the content, date, signup, and audience choices, while previously opened sections remain available.
 */
export function AdminCreatePostRedesignForm({
  templates,
  isErrorTemplates,
  templateInUse,
  onApplyTemplate,
  onClearTemplate,
  isUndoable,
  isRedoable,
  isCopilotAvailable,
  isGenerating,
  onGenerate,
  onUndo,
  onRedo,
  audience,
  selectedGroup,
  selectedProject,
  selectedProjectConnection,
  onSelectGroup,
  onSelectProject,
  onSelectProjectConnection,
  selectedScheduleDate,
  onSelectScheduleDate,
  onCancel,
  isSubmitting,
  canCreateEvent,
  canConfigureComments,
}: Props): React.ReactNode {
  const clockDate = useCurrentDate();
  const [openedSections, setOpenedSections] = useState<Section[]>([]);
  const openSection = (section: Section) => {
    setOpenedSections((sections) => (sections.includes(section) ? sections : [...sections, section]));
  };
  const { t } = useTranslation();
  const form = useFormContext<AdminCreatePostFormValues>();
  const { field: dateChoice } = useController({
    control: form.control,
    name: "hasDate",
    rules: {
      validate: (value) => !canCreateEvent || !!value || t("page.admin-create-post.redesign.answer-required"),
    },
  });
  const hasDate = dateChoice.value;
  const eventStartsAt = useWatch({ control: form.control, name: "eventStartsAt" });
  useController({
    control: form.control,
    name: "eventType",
    rules: {
      validate: (value) =>
        !canCreateEvent || hasDate !== "yes" || !!value || t("page.admin-create-post.redesign.answer-required"),
    },
  });
  useController({
    control: form.control,
    name: "scheduleDate",
    rules: {
      validate: (value) =>
        hasDate !== "yes" || !value || !eventStartsAt || value < eventStartsAt
          ? undefined
          : t("page.admin-create-post.schedule-selector.error.must-be-before-event"),
    },
  });
  const { errors } = useFormState({ control: form.control });
  const dateChoiceError = errors.hasDate?.message;
  const signupChoiceError = errors.eventType?.message;
  const dateError = errors.hasDate?.message || errors.eventStartsAt?.message || errors.eventEndsAt?.message;
  const signupError = errors.eventType?.message || errors.eventMaxParticipants?.message;
  const audienceError =
    errors.audienceType?.message || errors.audienceEntityId?.message || errors.scheduleDate?.message;
  const eventEndsAt = useWatch({ control: form.control, name: "eventEndsAt" });
  const eventType = useWatch({ control: form.control, name: "eventType" });
  const eventNoParticipantsLimit = useWatch({ control: form.control, name: "eventNoParticipantsLimit" });
  const postType = useWatch({ control: form.control, name: "postType" });
  const audienceType = useWatch({ control: form.control, name: "audienceType" });
  const audienceEntityId = useWatch({ control: form.control, name: "audienceEntityId" });

  const canConfigureSignup = hasDate === "yes" && !!eventStartsAt && !!eventEndsAt && eventEndsAt > eventStartsAt;

  const continueFromContent = async () => {
    const isValid = await form.trigger(["title", "content"]);
    if (isValid) {
      openSection(canCreateEvent ? "date" : "audience");
    }
  };

  const selectDateOption = (value: "yes" | "no") => {
    form.setValue("hasDate", value, { shouldDirty: true, shouldValidate: true });
    if (value === "yes" && !eventStartsAt && !eventEndsAt) {
      const dates = getDefaultEventDates();
      form.setValue("eventStartsAt", dates.start, { shouldDirty: true, shouldValidate: true });
      form.setValue("eventEndsAt", dates.end, { shouldDirty: true, shouldValidate: true });
    }
    if (value === "no") {
      form.clearErrors(["eventStartsAt", "eventEndsAt", "eventType", "eventMaxParticipants"]);
    }
    void form.trigger("scheduleDate");
  };

  const handleEventStartChange = (nextStart: Date | undefined) => {
    if (nextStart && nextStart < clockDate) {
      return;
    }
    const end = getEndDateAfterStartChange(eventStartsAt, form.getValues("eventEndsAt"), nextStart, (date) =>
      addHours(date, 1),
    );
    form.setValue("eventEndsAt", end, { shouldDirty: true, shouldValidate: true });
    void form.trigger("scheduleDate");
  };

  const selectEventType = (value: "announcement" | "common") => {
    form.setValue("eventType", value, { shouldDirty: true, shouldValidate: true });
    if (value === "announcement") {
      form.setValue("eventMaxParticipants", "");
      form.setValue("eventNoParticipantsLimit", true);
      form.setValue("eventAllowGuests", true);
      form.clearErrors("eventMaxParticipants");
    }
  };

  const setCommentsEnabled = (enabled: boolean) => {
    form.setValue("postType", enabled ? "announcement" : "announcementReadOnly", {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const selectCapacityMode = (mode: "limited" | "unlimited") => {
    const isUnlimited = mode === "unlimited";
    form.setValue("eventNoParticipantsLimit", isUnlimited, { shouldDirty: true, shouldValidate: true });
    if (isUnlimited) {
      form.setValue("eventMaxParticipants", "");
      form.clearErrors("eventMaxParticipants");
    }
  };

  return (
    <Stack gap="lg">
      {!isErrorTemplates && (
        <AdminCreatePostFormTemplateSelector templates={templates} onApplyTemplate={onApplyTemplate} />
      )}

      <InputFrame
        title={t("page.admin-create-post.redesign.content.title")}
        action={
          templateInUse ? (
            <Button size="sm" variant="ghostPrimary" onClick={onClearTemplate}>
              {t("page.admin-create-post.section.step-2.btn.clear-template", {
                templateName: templateInUse.name,
              })}
            </Button>
          ) : undefined
        }
      >
        <Stack gap="md">
          <AdminCreatePostFormEditor
            mentionAudience={{ type: audienceType ?? "project", id: audienceEntityId }}
            contentAction={
              <AdminCreatePostFormAIHelper
                presentation="editor"
                isUndoable={isUndoable}
                isRedoable={isRedoable}
                isCopilotAvailable={isCopilotAvailable}
                isGenerating={isGenerating}
                onGenerate={onGenerate}
                onUndo={onUndo}
                onRedo={onRedo}
                isDisabled={Boolean(templateInUse)}
              />
            }
          />
          {openedSections.length === 0 && (
            <Inline justify="end">
              <Button data-testid="continue-post-creation-btn" onClick={() => void continueFromContent()}>
                {t("common.action.continue")}
              </Button>
            </Inline>
          )}
        </Stack>
      </InputFrame>

      {canCreateEvent && (
        <InputFrame
          title={t("page.admin-create-post.redesign.date.title")}
          description={t("page.admin-create-post.redesign.date.description")}
          isOpen={openedSections.includes("date") || Boolean(dateError)}
          error={dateChoiceError}
          onOpen={() => openSection("date")}
          isDisabled={false}
        >
          <FormRadioCardGroup layout="vertical" legend={t("page.admin-create-post.redesign.date.title")}>
            <RadioCard
              name="hasDate"
              value="yes"
              checked={hasDate === "yes"}
              onChange={() => selectDateOption("yes")}
              label={t("page.admin-create-post.redesign.date.yes.label")}
              description={t("page.admin-create-post.redesign.date.yes.description")}
            />
            {hasDate === "yes" && (
              <Box padding={{ start: "md" }}>
                <Box padding={{ start: "xl", top: "sm", bottom: "md" }}>
                  <Stack gap="md">
                    <Inline gap="md" align="start">
                      <FormField label={t("page.admin-create-post.redesign.date.start")} required>
                        <FormDatePicker<AdminCreatePostFormValues, "eventStartsAt">
                          name="eventStartsAt"
                          data-testid="post-event-start-date-input"
                          onChange={handleEventStartChange}
                          type="datetime"
                          min={clockDate}
                          showNow={false}
                          rules={{
                            required: t("components.form.error.required", {
                              inputName: t("page.admin-create-post.redesign.date.start"),
                            }),
                            validate: (startDate) =>
                              !startDate || startDate >= clockDate
                                ? undefined
                                : t("model.event.start-date.error.must-be-in-future"),
                          }}
                        />
                      </FormField>
                      <FormField label={t("page.admin-create-post.redesign.date.end")} required>
                        <FormDatePicker<AdminCreatePostFormValues, "eventEndsAt">
                          name="eventEndsAt"
                          data-testid="post-event-end-date-input"
                          type="datetime"
                          min={eventStartsAt ? addMinutes(eventStartsAt, 1) : clockDate}
                          showNow={false}
                          rules={{
                            required: t("components.form.error.required", {
                              inputName: t("page.admin-create-post.redesign.date.end"),
                            }),
                            validate: (endDate) =>
                              !endDate || !eventStartsAt || endDate > eventStartsAt
                                ? undefined
                                : t("model.event.end-date.error.later-than-start-date"),
                          }}
                        />
                      </FormField>
                    </Inline>
                    <FormField label={t("page.event.create-or-edit.location")}>
                      <FormInput<AdminCreatePostFormValues>
                        name="eventLocation"
                        placeholder={t("page.event.create-or-edit.location.placeholder")}
                      />
                    </FormField>
                  </Stack>
                </Box>
              </Box>
            )}
            <RadioCard
              name="hasDate"
              value="no"
              data-testid="post-without-date-option"
              checked={hasDate === "no"}
              onChange={() => selectDateOption("no")}
              label={t("page.admin-create-post.redesign.date.no.label")}
              description={t("page.admin-create-post.redesign.date.no.description")}
            />
          </FormRadioCardGroup>
        </InputFrame>
      )}

      {canCreateEvent && (
        <InputFrame
          title={t("page.admin-create-post.redesign.signup.title")}
          description={t(
            canConfigureSignup
              ? "page.admin-create-post.redesign.signup.description"
              : "page.admin-create-post.redesign.signup.date-required",
          )}
          isOpen={(openedSections.includes("signup") || Boolean(signupError)) && canConfigureSignup}
          error={signupChoiceError}
          onOpen={() => openSection("signup")}
          isDisabled={!canConfigureSignup}
        >
          <FormRadioCardGroup layout="vertical" legend={t("page.admin-create-post.redesign.signup.title")}>
            <RadioCard
              name="eventType"
              value="common"
              checked={eventType === "common"}
              onChange={() => selectEventType("common")}
              label={t("page.admin-create-post.redesign.signup.yes.label")}
              description={t("page.admin-create-post.redesign.signup.yes.description")}
            />
            {eventType === "common" && (
              <Box padding={{ start: "md" }}>
                <Box padding={{ start: "xl", top: "sm", bottom: "md" }}>
                  <Stack gap="lg">
                    <Stack gap="sm" align="start">
                      <FormField
                        label={t("model.event.max-participants")}
                        description={t("model.event.max-participants.description")}
                      >
                        <SegmentGroup
                          width="content"
                          ariaLabel={t("model.event.max-participants")}
                          value={eventNoParticipantsLimit ? "unlimited" : "limited"}
                          onChange={selectCapacityMode}
                          items={[
                            {
                              value: "unlimited",
                              label: t("model.event.max-participants.mode.unlimited"),
                            },
                            {
                              value: "limited",
                              label: t("model.event.max-participants.mode.limited"),
                            },
                          ]}
                        />

                        {!eventNoParticipantsLimit && (
                          <FormInput<AdminCreatePostFormValues>
                            aria-label={t("model.event.max-participants")}
                            inputMode="numeric"
                            type="number"
                            step={1}
                            name="eventMaxParticipants"
                            width="numeric"
                            rules={{
                              required: {
                                message: t("components.form.error.required", {
                                  inputName: t("model.event.max-participants"),
                                }),
                                value: !eventNoParticipantsLimit,
                              },
                              min: {
                                message: t("model.event.max-participants.error.min"),
                                value: 1,
                              },
                              validate: (value) =>
                                value === "" ||
                                Number.isInteger(Number(value)) ||
                                t("model.event.max-participants.error.integer"),
                            }}
                          />
                        )}
                      </FormField>
                    </Stack>
                    <FormCheckbox<AdminCreatePostFormValues>
                      name="eventAllowGuests"
                      label={t("model.event.allow-guests")}
                      description={t("model.event.allow-guests.description")}
                    />
                  </Stack>
                </Box>
              </Box>
            )}
            <RadioCard
              name="eventType"
              value="announcement"
              checked={eventType === "announcement"}
              onChange={() => selectEventType("announcement")}
              label={t("page.admin-create-post.redesign.signup.no.label")}
              description={t("page.admin-create-post.redesign.signup.no.description")}
            />
          </FormRadioCardGroup>
        </InputFrame>
      )}

      <InputFrame
        title={t("page.admin-create-post.redesign.audience.title")}
        description={t("page.admin-create-post.redesign.audience.description")}
        isOpen={openedSections.includes("audience") || Boolean(audienceError)}
        onOpen={() => openSection("audience")}
        toggleTestId="post-audience-section-btn"
        isDisabled={false}
      >
        <Stack gap="xl">
          <Stack gap="md">
            <AdminCreatePostFormAudienceSelector
              audience={audience}
              selectedGroup={selectedGroup}
              onSelectGroup={onSelectGroup}
              selectedProject={selectedProject}
              selectedProjectConnection={selectedProjectConnection}
              onSelectProject={onSelectProject}
              onSelectProjectConnection={onSelectProjectConnection}
            />
            {canConfigureComments && (
              <Checkbox
                data-testid="allow-comments-checkbox"
                label={t("page.admin-create-post.redesign.comments.label")}
                description={t("page.admin-create-post.redesign.comments.description")}
                checked={postType === "announcement"}
                onChange={(event) => setCommentsEnabled(event.target.checked)}
              />
            )}
            <Inline gap="sm" align="center">
              <FormCheckbox
                label={t("page.admin-create-post.form.email-preview-only.label")}
                name="isEmailPreviewOnly"
              />
              <Info tooltip={t("page.admin-create-post.form.email-preview-only.info")} />
            </Inline>
          </Stack>
          <Spread space="sm" align="center">
            <Button variant="subtle" tone="error" onClick={onCancel}>
              {t("common.action.cancel")}
            </Button>
            <Inline gap="sm" align="center">
              <FormErrorWrapper<AdminCreatePostFormValues> name="scheduleDate" width="fit">
                <AdminCreatePostFormScheduleSelector
                  selectedDate={selectedScheduleDate}
                  onSelectDate={onSelectScheduleDate}
                  maxDate={hasDate === "yes" ? eventStartsAt : undefined}
                />
              </FormErrorWrapper>
              <Button type="submit" isLoading={isSubmitting}>
                {selectedScheduleDate
                  ? t("page.admin-create-post.form.btn.cta-schedule")
                  : t("page.admin-create-post.form.btn.cta-post")}
              </Button>
            </Inline>
          </Spread>
        </Stack>
      </InputFrame>
    </Stack>
  );
}
